/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable: no-loop-func max-file-line-count*/

// Core Libraries
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Interfaces
import {
  ProductDetails,
  ProductTypes,
  EpinConflictResponse,
  EpinCount,
  MixedCartObject,
} from '@experience/shop/product-details/interfaces/product-details.interface';
import {
  ColorOption,
  ItemDetail,
  MemoryOption,
  ProductDetail,
  Promotion,
  PricingOptions,
  Specification,
  DeviceSimInfo,
} from '@support/api/common/product.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { EntryModalInterface } from '@experience/shop/shared-browse/interfaces/entry-modal.interface';
import { APIError } from '@support/api/common/error.interface';
import { LineDetails } from '@support/api/proxies/responses/api-2007.interface';
import {
  LineSummary,
  CustomerAccountBillingDetails,
} from '@support/api/proxies/responses/api-2006.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';

// services
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
import { UserService } from '@support/shared/services/user.service';
// Constants
import { PRODUCT_OFFERINGS_CONSTANTS, BUTTON_CONSTANTS } from './product-offerings.constants';
import { PRODUCT_DETAILS_CONST } from '@experience/shop/product-details/product-details.constants';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';

// Models
import { SETTINGS } from '@environment/settings';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { FAN } from '@support/api/proxies/responses/api-2038.interface';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { SelectItem } from 'primeng/api';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { AddDeviceToNonLineItemsRequestModel } from 'app/model/add-nonline-device-request.model';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { CART_EVENT_NAME } from '@support/shared/constants/pdl-data.constants';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

// TODO: will use in future

@Component({
  selector: 'tmo-product-offerings',
  templateUrl: './product-offerings.component.html',
  styleUrls: ['../../../../../style-sheets/components/input-radio.component.scss'],
})
export class ProductOfferingsComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Stores the value of cart data
   */
  public cartData: Cart;
  /**
   * Stores existing lines in account
   */
  public cartExistingLines: LineSummary[];
  /**
   * Stores device details coming from API call
   */
  public deviceDetails: ProductDetails;
  /** Boolean to show device is not added error */
  public showDeviceAddError: boolean;
  /**
   * Stores the content coming from AEM call
   */
  public pdpStaticContent: ConfigData;

  /**
   * To store Product Availability types constant that will be later used in Html
   */
  public productAvailability: {};
  /**
   * To store Product details constant that will be later used in Html
   */
  public productDetailsConstant: {};
  /**
   * Stores the cartId as string.
   */
  public cartId: string;
  /**
   * It is used to check is accessory sections on pdp page.
   */
  public isAccessoriesCarousel: boolean;
  /**
   * It is used to check is pdp screen loads from warranty replacement page.
   */
  public isWarrantyReplacementFlow: boolean;
  /**
   * showModal boolean to open and close the modal
   */
  public deviceErrorModal: EntryModalInterface;
  /**
   * Flag to check if EIP is Ineligible.
   */
  public isEipIneligible: boolean;
  /**
   * Disable device update button on successful api posting to cart.
   */
  public disableButton: boolean;
  /**
   * Stores if assisted flow
   */
  // public isAssistedFlow: boolean;
  /**
   * Stores Line details for the current line
   */
  public lineDetails: LineDetails;
  /**
   * Holds whether to disable go to cart button
   */
  public disableGoToCartButton: boolean;
  /**
   * Holds whether it is more discount
   */
  public isMoreDiscount: boolean;
  /**
   * Holds the value of promotions array
   */
  public promotions: Promotion[];
  /**
   * Holds the value of allowEdit boolean
   */
  public allowEdit: boolean;
  /**
   * Holds the selected item to bind to the screen
   */
  public selectedItem: ItemDetail;
  /**
   * Holds all the available color options from itemDetails
   */
  public colorOptions: ColorOption[];
  /**
   * Holds all available memory options
   */
  public memoryOptions: MemoryOption[];
  /**
   * Holds all available amount to display in dropdown
   */
  public pricingOptions: PricingOptions[];
  /**
   * Holds all item details for the current offerId type (to be used to populate the selectedItem)
   */
  public itemDetails: ItemDetail[];
  /**
   * Holds ngUnsubscribe Object
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Holds boolean to show or hide spinner
   */
  public showSpinner: boolean;
  /**
   * Holds external domain URL for images
   */
  public extTmoImagesUrl: string;
  /**
   * Holds Error Message
   */
  public errorMessage: APIError[];
  /**
   * Holds focused Tile Id Details
   */
  public focusedTileIdDetails: LineDeivceDetails;
  /**
   * Holds value of product details model
   */
  public product: ProductDetailsDefaultModel;
  /**
   * Boolean to control popup
   */
  public showItemAddedPopUp: boolean;
  /**
   * selected quantity of the items
   */
  public selectedQuantity: number;
  /**
   * Add to Cart Button
   */
  public addToCart: TmoButton;
  /**
   * Checkout button
   */
  public checkOut: TmoButton;
  /**
   * stores boolean value of epin
   */
  public isEpin: boolean;
  /**
   * Holds amount selected from dropdown
   */
  public selectedEpinAmount: number;
  /**
   * holds dropdown values
   */
  public pricingListDropdown: SelectItem[];
  /**
   * Holds the image paths
   */
  public cacheImages: string[];

  /**
   * Holds boolean if product type is financial
   */
  public isProductTypeFinancial: boolean;
  /**
   * Holds boolean if product type is Accessory
   */
  public isProductTypeAccessory: boolean;
  /**
   * Holds epinConflictData
   */
  public epinConflictData: EpinConflictResponse;
  /**
   * product faily name
   */
  public productFamilyName: string;
  /**
   * Holds checkoutAccessory boolean value
   */
  public checkoutAccessory: boolean;
  /**
   * Holds the value of channel
   */
  // public channel: string;
  /**
   * Stores the selected device or accessory amount
   */
  public selectedAmount: number;
  /**
   * Holds boolean and sets to true if product subtype is simcard
   */
  public isSimCard: boolean;
  /**
   * interface to fetch epin count
   */
  public epinCount: EpinCount;
  /**
   * check if user authenticated
   */
  public isAuthFlow: boolean;
  /**
   *  to show existing customer modal
   */
  public showExistingCustomerModal: boolean;
  /**
   * variable to show modal
   */
  public displayUpgradeMultilineModal: boolean;
  /**
   * variable to hide spinner
   */
  public hideSpinner: boolean;
  /**
   * string to hold cart item id if editing a non line device
   */
  private cartItemId: string;
  /**
   * boolean to hold if user is in device upgrade flow
   */
  public isUpgradeFlow: boolean;
  /**
   * boolean to check if user cart is empty
   */
  public isCartEmpty: boolean;
  /**
   * boolean to check if cart order type is PURCHASEEQUIPMENT
   */
  public isPurchaseEquipment: boolean;
  /**
   * boolean to check if user has added anything to cart which is applicable for device upgrade scenario
   */
  public isUpgradeCart: boolean;
  /**
   * boolean to check if user is editing non line device in upgrade device flow
   */
  public isEditingNonLineDevice: boolean;
  /**
   * boolean to check if user is editing line device in upgrade device flow
   */
  public isEditingUpgradeLine: boolean;
  /**
   * Holds upgrade API error message
   */
  public upgradeErrorMessage: APIError[];
  /**
   * Holds the details for mixedCartModal
   */
  public mixedCartModal: StaticModal;
  /**
   * Holds if mixedCart is there
   */
  public mixedCart: boolean;
  /**
   * Holds the selectedProductDetails data
   */
  private selectedProductDetails: ProductDetails;
  /**
   * Holds if it is selectedProductDeviceUpgrade
   */
  private selectedProductDeviceUpgrade: boolean;
  /**
   * Holds the mixedCartObject
   */
  public mixedCartObject: MixedCartObject;
  /**
   * Holds product availabilty status
   */
  public isProductAvailable: boolean;
  /**
   * Holds selected Item Details
   */
  public selectedItemDetails: Specification[];

  public existingLineDetailsAvailable: boolean;
  /**
   * Holds boolean if rep has permission to add second line or not
   */
  public hasPermissionToAddALine: boolean;
  /**
   * Holds whether customer is p10m customer or not
   */
  private isP10MCustomer: boolean;
  /**
   * Disable add to cart if in store flow.
   */
  public disableCta: boolean;

  public pdlData: object;

  public modal: DynamicModal;

  private isDeviceUpgradeFlag: boolean = false;

  private isDeviceReplaceFlag: boolean = false;

  private isNewCustomer: boolean = false;
  /**
   * To check whether accessory page cta is clicked.
   */
  private isAccessory: boolean = false;

  // private isWebChannel: boolean;

  private skipServiceForPlanFirst: boolean = true;

  constructor(
    private tileManagementService: TileManagementService,
    private productDetailsService: ProductDetailsService,
    private currencyPipe: CurrencyPipe,
    private stateUtilService: StateStoreUtilsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    private commonUtils: CommonUtilService,
    private userService: UserService,
    public pdlWrapperService: PdlWrapperService,
    private pdpDataHandlerService: PdpDataHandlerService,
    private featureToggleUtilService: FeatureToggleUtilService,
    public pdlService: PdlService
  ) {
    super(pdlService, activatedRoute);
    this.isAccessoriesCarousel = false;
    // this.channel = SETTINGS.CHANNEL.toLowerCase();
    this.isMoreDiscount = false;
    this.disableGoToCartButton = false;
    this.hideSpinner = true;
    this.ngUnsubscribe = new Subject<void>();
    this.colorOptions = [];
    this.memoryOptions = [];
    this.extTmoImagesUrl = SETTINGS.EXT_IMAGE_URL;
    this.populateButtonData();
    this.displayUpgradeMultilineModal = false;
    this.isProductAvailable = true;
    this.epinCount = {
      deviceDetails: {
        offerId: '',
      },
      selectedEpinAmount: '',
      selectedItem: {
        sku: '',
      },
      selectedQuantity: 0,
    };
    this.mixedCart = false;
    this.mixedCartObject = <MixedCartObject>new Object();
    const guestCheck =
      this.stateUtilService.getCustomerId() === SHARED_BROWSE_CONSTANTS.ANON ||
      this.stateUtilService.getCustomerId() === SHARED_BROWSE_CONSTANTS.EMPTY_STRING;
    this.isAuthFlow =
      this.stateUtilService.getCustomerId() && !this.stateUtilService.getAddFan() && !guestCheck;
    this.focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
    this.productAvailability = PRODUCT_OFFERINGS_CONSTANTS;
    this.productDetailsConstant = PRODUCT_DETAILS_CONST;
    this.deviceErrorModal = {
      modalName: 'product-offerings-modal',
    };
    this.disableButton = false;
    // this.isAssistedFlow = SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.WEB;
    this.showDeviceAddError = this.cartSupportService.isStoreOpen();
    this.userService.p10CustomerLoggedIn$.subscribe(
      (isP10MCustomer: boolean) => (this.isP10MCustomer = isP10MCustomer)
    );
  }

  /**
   * loads the initial state of product offerings component
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.createModal();
    this.getExistingLineDetails();
    this.allowEdit =
      this.activatedRoute.snapshot.queryParams.allowEdit === 'true' ||
      !this.objectService.get(this.focusedTileIdDetails, 'isLineSetupCompleted', true);
    this.hasPermissionToAddALine =
      this.cartSupportService.checkForAddALinePermission(PERMISSION_CONSTANTS.DEVICE_PDP) ||
      this.allowEdit;
    if (this.activatedRoute.snapshot.queryParams.changeDevice) {
      this.selectedItem = this.stateUtilService.getSelectedItemInPDP(
        this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.sku')
      );
    }
    this.loadProductDetails();
    this.isWarrantyReplacementFlow = this.productDetailsService.isWarrantyReplacementFlow();
    this.activatedRoute.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: Params) => {
      this.cartItemId = params.cartItemId;
    });
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params: Params) => {
        this.isUpgradeFlow = params.isUpgrade === 'true' || this.stateUtilService.getUpgradeFlow();
        this.isEditingUpgradeLine = params.isEditingUpgradeLine === 'true';
        this.isEditingNonLineDevice = params.isEditingNonLineDevice === 'true';
      });
    this.isUpgradeCart = this.cartSupportService.isDeviceUpgradeFlow();
    const cart = this.stateUtilService.getCart();
    this.isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.isPurchaseEquipment =
      this.objectService.get(cart, 'orderType') === ORDER_TYPE.PURCHASEEQUIPMENT;
    const offerId = this.stateUtilService.getSelectedOfferId();
    if (offerId) {
      this.product = this.stateUtilService.getSelectedDeviceDetails(offerId);
      this.selectedItemDetails = this.product.getProductSpecifications();
    }
    // this.isWebChannel = this.channel.toLowerCase() === CHANNELS.WEB;
    // if (this.isWebChannel) {
    this.pdpDataHandlerService.updateCartForPDL(cart, this.isCartEmpty);
    // }
  }

  /**
   * Method to check conflict for Epin & disable CTAs
   * @returns boolean
   */
  private disableCtaForEpin(): boolean {
    // const isRetail = this.channel.toLowerCase() === CHANNELS.RETAIL;
    const productTypeEnum = this.isEpin ? ProductTypes.EPIN : ProductTypes.HARDGOOD;
    const epinConflictData = this.cartSupportService.isEpinConflictWithCart(false, productTypeEnum);
    const hasConflict = this.objectService.get(epinConflictData, 'hasConflict', false);
    // const isInstoreFlow = this.stateUtilService.getInstoreFlow();
    // return  isRetail && (isInstoreFlow && !this.isEpin) || hasConflict;
    return hasConflict;
  }

  /**
   * Method to load Product Details
   * @param deviceDetail Product details
   * @param defaultColor Selected default color for the product
   * @param defaultMemory Selected default memory for the product
   * @param priceOption Available payment options
   * @returns void
   */
  public loadProductDetails(): void {
    this.productDetailsService
      .getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.pdpStaticContent = content;
        this.mixedCartModal.headerText = this.objectService.get(
          this.pdpStaticContent,
          'clearYourCart'
        );
        this.mixedCartModal.bodyText = this.objectService.get(
          this.pdpStaticContent,
          'clearOrViewCart'
        );
        this.mixedCartModal.buttons[0].id = this.objectService.get(
          this.pdpStaticContent,
          'continue'
        );
        this.mixedCartModal.buttons[0].name = this.objectService.get(
          this.pdpStaticContent,
          'continue'
        );
        this.mixedCartModal.buttons[1].id = this.objectService.get(
          this.pdpStaticContent,
          'viewCart'
        );
        this.mixedCartModal.buttons[1].name = this.objectService.get(
          this.pdpStaticContent,
          'viewCart'
        );
        const offerId = this.stateUtilService.getSelectedOfferId();
        if (offerId) {
          this.setProductDataFromModel(offerId);
          // this.updateDefaultSimSkuSelection();
          this.createEpinPricingDropdown();
        }
        this.disableCta = this.disableCtaForEpin();
      });
  }

  /**
   * This method updates default Sim radio selection for asisted channel when sim is edited
   * @returns void
   */
  // public updateDefaultSimSkuSelection(): void {
  //   if (this.allowEdit && this.isSimCard && this.isAssistedFlow){
  //     const skuId = this.objectService.get(this.focusedTileIdDetails, 'deviceDetails.sku');
  //     this.handleSimRadioClickEvent(skuId);
  //   }
  // }

  /**
   * update the selected color of device
   * @param color passes the selected color
   * @returns void
   */
  public updateSelectedColor(color: string): void {
    this.stateUtilService.setSelectedColor(color);
    this.selectedItem = this.product.setSelectedColorVarient(this.objectService, color);
    this.pdlWrapperService.trackData('Product-Detail', 'product', {
      productUpdate: {
        productInfo: {
          sku: this.selectedItem.id,
          availability: this.selectedItem.availabilityStatus,
          price: this.selectedItem.salePrice,
        },
      },
    });
    this.memoryOptions = this.product.getMemoryOptions();
  }

  /**
   * update the selected memory of device
   * @param memory passes the selected memory
   * @returns void
   */
  public updateSelectedMemory(memory: string): void {
    this.selectedItem = this.product.setSelectedMemoryVarient(memory, this.objectService);
    this.pdlWrapperService.trackData('Product-Detail', 'product', {
      productUpdate: {
        productInfo: {
          sku: this.selectedItem.id,
          availability: this.selectedItem.availabilityStatus,
          price: this.selectedItem.salePrice,
        },
      },
    });
  }

  /**
   * update the selected sim info of device
   * @param simInfo passes the selected DeviceSimInfo
   */
  public updateSelectedSimInfo(simInfo: DeviceSimInfo): void {
    this.stateUtilService.setSelectedSimInfo(simInfo);
    this.pdlWrapperService.trackData('Product-Detail', 'product', {
      productUpdate: {
        productInfo: {
          sku: this.selectedItem.id,
          availability: this.selectedItem.availabilityStatus,
          price: this.selectedItem.salePrice,
        },
      },
    });
  }

  /**
   * update the selected sim sku of device
   * @param simSku sku id of simcard
   * @returns void
   */
  public handleSimRadioClickEvent(simSku: string): void {
    this.selectedItem = this.product.getItemDetailsByDeviceSku(simSku, this.objectService);
  }

  /**
   * Retreive certified pre owned device
   * @param hasCPO
   * @returns void
   */
  public getCertifiedDevice(hasCPO: boolean): void {
    this.showSpinner = true;
    const itemDetailsFromCache = this.objectService.get(
      this.stateUtilService.getProductDetails(this.deviceDetails.offerId),
      'itemDetails'
    );
    this.productDetailsService
      .getProductDetails(
        this.deviceDetails.offerId,
        this.deviceDetails.type,
        hasCPO,
        itemDetailsFromCache ? 'true' : 'true'
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: ProductDetail) => {
          if (this.objectService.get(response, 'model-view')) {
            const productData = this.objectService.get(
              response,
              'model-view.model.model-data.product-offerings.productDetailsResponse'
            );
            const productDetails = productData || null;
            const productOfferId = this.objectService.get(productData, 'familyId');
            this.stateUtilService.setSelectedOfferId(productOfferId);
            const selectedColor = this.stateUtilService.getSelectedColor();
            const productDetailsModel = new ProductDetailsDefaultModel(
              productDetails,
              this.objectService,
              selectedColor,
              this.commonUtils
            );
            productDetailsModel.setCpoClicked();
            this.stateUtilService.setSelectedDeviceDetails(productDetailsModel, productOfferId);
            this.setProductDataFromModel(productOfferId);
          }
          this.showSpinner = false;
        },
        (error: APIError[]) => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * update the selected quantity of device
   * @param memory passes the selected memory
   * @returns void
   */
  public updateSelectedQuantity(quantity: number): void {
    this.selectedQuantity = quantity;
  }

  /**
   * Method used to destroy data, subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method used on cross function to set epin object to null
   * @returns void
   */
  public closeModal(): void {
    this.epinConflictData = {};
  }
  /**
   * Close customer modal on click of cross button
   * @return void
   */
  public closeCustomerModal(): void {
    this.showExistingCustomerModal = false;
  }
  /**
   * refresh Focused Till data
   * @return void
   */
  public refreshFocusedTill(): void {
    this.focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
  }

  /**
   * sets the selected device details and navigates to product-selector page
   * @ngdoc method
   * @name checkDeviceError
   * @returns void
   */
  public checkDeviceError(
    productDetails: ProductDetails,
    isDeviceUpgrade?: boolean,
    isUpgradeModal?: boolean
  ): void {
    // this.setFlags(isDeviceUpgrade, isUpgradeModal);
    // if (!this.isAssistedFlow && !this.isProductTypeAccessory && !this.isAuthFlow &&
    //   !this.cartSupportService.isCartNotEmpty(this.stateUtilService.getCart(), false)) {
    if (
      !this.isProductTypeAccessory &&
      !this.isAuthFlow &&
      !this.cartSupportService.isCartNotEmpty(this.stateUtilService.getCart(), false) &&
      !sessionStorage.getItem('customerSelection')
    ) {
      this.showExistingCustomerModal = true;
      this.isNewCustomer = true;
    } else {
      const lineId = this.stateUtilService.getSelectedUpgradeLineId();
      if (isUpgradeModal && !lineId) {
        this.showMultilineUpgradeModal(productDetails, isDeviceUpgrade);
      } else {
        this.setSelectedItemInPDP(productDetails, isDeviceUpgrade);
      }
    }
  }

  /**
   * method to set selected item in pdp
   * @param productDetails ProductDetails
   * @param isDeviceUpgrade boolean
   */
  public setSelectedItemInPDP(
    productDetails: ProductDetails,
    isDeviceUpgrade?: boolean,
    overrideIndicator?: string
  ): void {
    this.refreshFocusedTill();
    this.selectedProductDetails = productDetails;
    this.selectedProductDeviceUpgrade = isDeviceUpgrade;
    this.showExistingCustomerModal = false;
    this.productFamilyName = this.objectService.get(this.selectedProductDetails, 'familyName');
    const productTypeEnum = this.isEpin ? ProductTypes.EPIN : ProductTypes.HARDGOOD;
    this.epinConflictData = this.cartSupportService.isEpinConflictWithCart(false, productTypeEnum);
    this.selectedAmount = this.objectService.get(this.selectedItem, 'salePrice', 0);
    if (
      !(
        this.objectService.get(this.epinConflictData, 'hasConflict') ||
        this.objectService.get(this.epinConflictData, 'hasOnlyByos')
      )
    ) {
      this.showSpinner = true;
      if (
        (this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.cartItemId') &&
          this.allowEdit) ||
        isDeviceUpgrade
      ) {
        if (this.selectedItem) {
          this.stateUtilService.setSelectedItemInPDP(this.selectedItem);
        }
        if (this.isEditingNonLineDevice) {
          this.converNonLineItemToLineItem(overrideIndicator);
        } else {
          this.updateDeviceToCurrentPlan(isDeviceUpgrade, overrideIndicator);
        }
      } else {
        this.postDeviceAsNonLineItem(overrideIndicator);
      }
    }
  }

  /**
   * delete the current cart and add new selection to the current
   * @ngdoc method
   * @name addNewSelectionInCart
   * @returns void
   */
  public addNewSelectionInCart(): void {
    this.showSpinner = true;
    this.cartSupportService
      .deleteCart()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.showSpinner = false;
          this.stateUtilService.clearTileIdList(null, true);
          this.stateUtilService.clearServiceThresholdValue();
          this.stateUtilService.setNewPlansPage(true);
          // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL){
          //   this.stateUtilService.clearShipFulfillment();
          // }
          const productSubType = this.product.getProductSubType() || '';
          if (
            productSubType.toLowerCase() === PRODUCT_OFFERINGS_CONSTANTS.HANDSET ||
            productSubType.toLowerCase() === PRODUCT_OFFERINGS_CONSTANTS.TABLET ||
            productSubType.toLowerCase() === PRODUCT_OFFERINGS_CONSTANTS.MOBILE_INTERNET
          ) {
            this.postDeviceAsNonLineItem();
          } else {
            this.postAcessory(this.checkoutAccessory);
          }
        },
        (error: APIError[]) => {
          this.errorMessage = error;
          this.showSpinner = false;
        }
      );
  }

  /**
   * Function to update device to line in focus
   */
  private updateDeviceToCurrentPlan(isDeviceUpgrade?: boolean, overrideIndicator?: string): void {
    if (isDeviceUpgrade) {
      this.deleteNonLineTileWithoutDevice();
      this.setExisitingTileInFocus();
    }
    const isAllowEdit = this.allowEdit && !!this.focusedTileIdDetails;
    this.cartSupportService
      .updateLineDevice(
        this.selectedItem.id,
        this.deviceDetails.offerId,
        isDeviceUpgrade,
        null,
        null,
        null,
        overrideIndicator,
        this.selectedItem.uSkuIndicator,
        null,
        isAllowEdit
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data: Cart) => {
          this.showSpinner = false;
          if (isDeviceUpgrade) {
            this.stateUtilService.setUpgradeFlow(isDeviceUpgrade);
          }
          this.callTriggerCartEvent();
          this.tileManagementService.mapToCartResponse(data);
          this.navigateToProductSelector(isDeviceUpgrade);
        },
        (error: APIError[]) => {
          this.showSpinner = false;
          if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
            this.mixedCart = true;
            this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
          } else {
            this.errorMessage = error;
            this.upgradeErrorMessage = error;
          }
        }
      );
  }

  /**
   * Function to add device as non line item
   */
  public postDeviceAsNonLineItem(overrideIndicator?: string): void {
    this.showSpinner = true;
    if (this.isEditingUpgradeLine) {
      const cartItemId = this.objectService.get(
        this.focusedTileIdDetails,
        'deviceDetails.cartItemId'
      );
      const lineId = this.objectService.get(this.focusedTileIdDetails, 'lineId');
      const newSku = this.objectService.get(this.selectedItem, 'id');
      const newOfferId = this.stateUtilService.getSelectedOfferId();
      const cart = this.stateUtilService.getCart();
      const transactionType = this.objectService.get(cart, 'orderType');
      this.cartSupportService
        .updateLineLevelDeviceToCartLevel(
          cartItemId,
          lineId,
          newSku,
          newOfferId,
          transactionType,
          PRODUCT_OFFERINGS_CONSTANTS.UPGRADE_DEVICE,
          overrideIndicator
        )
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (cartData: Cart) => {
            this.objectService.set(this.focusedTileIdDetails, 'selectedDevice', null);
            this.objectService.set(this.focusedTileIdDetails, 'isUpgrade', false);
            this.stateUtilService.setFocusedTileLineNonLineItem(this.focusedTileIdDetails);
            this.tileManagementService.createTile(lineTypeEnum.NONLINE, true);
            this.callTriggerCartEvent();
            this.processNonLineItemCartResponse(cartData);
          },
          (error: APIError[]) => {
            this.showSpinner = false;
            if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
              this.mixedCart = true;
              this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
            } else {
              this.errorMessage = error;
            }
          }
        );
    } else {
      const skuItemCollection = [];
      this.refreshFocusedTill();
      const isAllowEdit = this.allowEdit && !!this.focusedTileIdDetails;
      const skuItem = new AddDeviceToNonLineItemsRequestModel();
      this.selectedQuantity = this.selectedQuantity ? this.selectedQuantity : 1;
      skuItem.setSkuDetails(
        this.selectedItem.id,
        this.deviceDetails.offerId,
        null,
        null,
        this.selectedQuantity.toString()
      );
      skuItemCollection.push(skuItem.getDeviceInfo());
      this.cartSupportService
        .postDeviceAsNonLineItem(
          skuItemCollection,
          isAllowEdit,
          null,
          null,
          overrideIndicator,
          this.selectedItem.uSkuIndicator,
          null,
          isAllowEdit
        )

        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (cartData: Cart) => {
            if (!isAllowEdit) {
              const hasPermissionToAddALine =
                this.hasPermissionToAddALine &&
                !(
                  this.objectService.get(this.focusedTileIdDetails, 'lineTileType') ===
                    lineTypeEnum.NONLINE &&
                  this.objectService.get(this.focusedTileIdDetails, 'isLineSetupCompleted')
                );
              const noPlanNeeded = this.isUpgradeFlow
                ? true
                : this.cartSupportService.isMaxLineReached() || !hasPermissionToAddALine;
              this.tileManagementService.createTile(lineTypeEnum.NONLINE, noPlanNeeded);
            }
            if (this.isUpgradeFlow) {
              this.stateUtilService.setUpgradeFlow(true);
            }
            this.callTriggerCartEvent();
            this.processNonLineItemCartResponse(cartData);
          },
          (error: APIError[]) => {
            this.showSpinner = false;
            if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
              this.mixedCart = true;
              this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
            } else {
              this.errorMessage = error;
            }
          }
        );
    }
  }

  /**
   * Function used to get existing line details
   */
  private getExistingLineDetails(): void {
    if (this.isAuthFlow) {
      this.existingLineDetailsAvailable = true;
      this.cartSupportService
        .getExistingLineDetails()
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (cart: FAN) => {
            this.existingLineDetailsAvailable = false;
            this.hasPermissionToAddALine = this.cartSupportService.checkForAddALinePermission(
              PERMISSION_CONSTANTS.DEVICE_PDP
            );
          },
          (error: APIError[]) => {
            this.errorMessage = error;
            this.existingLineDetailsAvailable = false;
          }
        );
    }
  }
  /**
   * Function used to navigate to product selector
   */
  // tslint:disable-next-line:cyclomatic-complexity
  private navigateToProductSelector(isDeviceUpgrade?: boolean): void {
    this.stateUtilService.setSelectedDeviceDetails(this.product, this.product.offerId);
    let tileId;
    if (this.allowEdit || isDeviceUpgrade) {
      const losId = this.stateUtilService.getSelectedUpgradeLineId();
      tileId =
        isDeviceUpgrade && this.tileManagementService.getExisitingLineTileId(losId)
          ? this.tileManagementService.getExisitingLineTileId(losId)
          : this.stateUtilService.getFocusedTileId();
      const details = this.stateUtilService.getFocusedTileLineNonLineItem(tileId);
      details.isUpgrade = isDeviceUpgrade;
      this.stateUtilService.setFocusedTileId(tileId);
      this.stateUtilService.setFocusedTileLineNonLineItem(details);
    } else {
      const nonItemTileIdList = this.stateUtilService.getTileIdList(lineTypeEnum.NONLINE);
      tileId = nonItemTileIdList.slice(-1)[0];
      this.stateUtilService.setFocusedTileId(tileId);
    }
    const isdormancyEnable = this.featureToggleUtilService.isNewProductSelectorTemplateEnabled();
    const isPurchaceClick =
      isdormancyEnable &&
      this.isAuthFlow &&
      (this.isUpgradeFlow || isDeviceUpgrade) &&
      !this.isEditingNonLineDevice;
    if (
      this.isP10MCustomer ||
      isPurchaceClick ||
      (this.skipServiceForPlanFirst && this.allowEdit)
    ) {
      this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.CART]);
    } else {
      this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR, tileId]);
    }
  }

  /**
   * Handle the click event for device selection
   * @param device offerId
   * @returns void
   */
  public navigateToDevicePdp(deviceOfferId: string): void {
    this.router.navigate(['/product-details', deviceOfferId]);
  }

  /**
   * Method is used to update promotions based on selected pricing option
   * @ngdoc method
   * @param promotions pricing promotions
   * @name selectedPromotions
   * @returns void
   */
  public selectedPromotions(promotions: Promotion[]): void {
    this.isMoreDiscount = false;
    this.promotions = promotions;
  }

  /**
   * To show all available discounts
   * @returns void
   */
  public showAllDiscounts(): void {
    this.isMoreDiscount = !this.isMoreDiscount;
  }

  /**
   * Handle method to check eip is eligible or not on the basis of change sku
   * @param isIneligible of type boolean
   * @returns void
   */
  public checkEipIneligible(isEipIneligible: boolean): void {
    this.isEipIneligible = isEipIneligible;
  }

  private setProductDataFromModel(offeringId: string): void {
    this.product = this.stateUtilService.getSelectedDeviceDetails(offeringId);
    this.deviceDetails = this.product.getProductDetails();
    this.isProductTypeFinancial =
      this.deviceDetails.type.toLowerCase() === HARDGOODS_CONSTANTS.FINANCIAL;
    // US431608 : Tourist Sim Kit should act like accessory
    this.isProductTypeAccessory =
      this.deviceDetails.type.toLowerCase() === HARDGOODS_CONSTANTS.ACCESSORY ||
      this.deviceDetails.offerId === HARDGOODS_CONSTANTS.TOURIST_SIM_KIT;
    this.itemDetails = this.product.getItemDetails();
    this.colorOptions = this.product.getColorOptions();
    this.isEpin = this.product.isEpin();
    this.cacheImages = this.product.getCacheImages();
    this.isSimCard = this.product.getProductSubType().toUpperCase() === HARDGOODS_CONSTANTS.SIMCARD;
    const memory = this.activatedRoute.snapshot.paramMap.get('memory');
    this.selectedItem = !memory
      ? this.product.setSelectedColorVarient(this.objectService)
      : this.product.setSelectedMemoryVarient(memory, this.objectService);
    this.memoryOptions = this.product.getMemoryOptions();
    this.pricingOptions = [];
    this.pricingOptions = this.product.getPricingOptions(this.commonUtils) || [];
  }

  /**
   * adds the accessory to cart or updates the quanity if the accessory already existsaddAccessoriesToCart
   * @param isCheckout flag to identify if we are checking out an accessory
   * @param overrideIndicator boolean flag to pass in headers if required
   * @returns void
   */
  // tslint:disable cyclomatic-complexity
  public addAccessoriesToCart(isCheckout: boolean, overrideIndicator?: boolean): void {
    this.showSpinner = false;
    // const isRetail = this.channel.toLowerCase() === CHANNELS.RETAIL;
    this.productFamilyName = this.objectService.get(this.deviceDetails, 'familyName');
    this.selectedAmount = this.objectService.get(this.selectedItem, 'salePrice') || 0;
    const productTypeEnum = this.isEpin ? ProductTypes.EPIN : ProductTypes.HARDGOOD;
    this.epinConflictData = this.cartSupportService.isEpinConflictWithCart(false, productTypeEnum);
    this.checkoutAccessory = isCheckout;
    if (!this.objectService.get(this.epinConflictData, 'hasConflict')) {
      if (this.isEpin) {
        this.createEpinDetails();
        this.stateUtilService.setEpinCart(this.epinCount);
        // if (isRetail || this.stateUtilService.getAccountId() || this.stateUtilService.getEpinMsisdn() ||
        //   !this.checkoutAccessory) {
        if (
          this.stateUtilService.getAccountId() ||
          this.stateUtilService.getEpinMsisdn() ||
          !this.checkoutAccessory
        ) {
          this.showSpinner = true;
          const skuItemCollection = [];
          const skuItem = new AddDeviceToNonLineItemsRequestModel();
          skuItem.setSkuDetails(
            this.epinCount.selectedItem.sku,
            this.epinCount.deviceDetails.offerId,
            null,
            null,
            this.objectService.get(this.epinCount, 'selectedQuantity', '').toString(),
            this.epinCount.selectedEpinAmount,
            this.cartItemId
          );
          skuItemCollection.push(skuItem.getDeviceInfo());

          this.cartSupportService
            .postAccessoryToCart(skuItemCollection)
            .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
            .subscribe(
              (cartResponse: Cart) => {
                this.showSpinner = false;
                this.navigateCheckout(isCheckout);
                this.callTriggerCartEvent();
              },
              (error: APIError[]) => {
                this.showSpinner = false;
                if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
                  this.mixedCart = true;
                  this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
                  this.mixedCartObject.isAccessory = true;
                  this.mixedCartObject.isAccessoryCheckout = isCheckout;
                } else {
                  this.errorMessage = error;
                }
              }
            );
        } else {
          this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.VALIDATE_MSISDN]);
        }
      } else {
        this.postAcessory(isCheckout, overrideIndicator);
      }
    }
  }
  /**
   * adds the accessory to cart after conflict check
   * @param isCheckout isCheckout to check whether to navigate to cart or not
   * @param overrideIndicator (optional) if passed, pass override indicator in header of API call
   * @returns void
   */
  private postAcessory(isCheckout: boolean, overrideIndicator?: boolean): void {
    this.showSpinner = true;
    const tempSelectedEipAmount = this.selectedEpinAmount
      ? this.selectedEpinAmount.toString()
      : undefined;

    const skuItemCollection = [];
    const skuItem = new AddDeviceToNonLineItemsRequestModel();
    skuItem.setSkuDetails(
      this.selectedItem.sku || this.selectedItem.id,
      this.deviceDetails.offerId,
      null,
      null,
      this.selectedQuantity.toString(),
      tempSelectedEipAmount
    );
    skuItemCollection.push(skuItem.getDeviceInfo());
    this.isAccessory = true;
    this.productDetailsService
      .addAccessoryToCart(skuItemCollection, overrideIndicator)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (cartResponse: Cart) => {
          this.showSpinner = false;
          this.callTriggerCartEvent();
          this.navigateCheckout(isCheckout);
        },
        (error: APIError[]) => {
          this.showSpinner = false;
          if (error[0].code === 'BIZ_0001_INVALID_TRANS_TYPE') {
            this.mixedCart = true;
            this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
            this.mixedCartObject.isAccessory = true;
            this.mixedCartObject.isAccessoryCheckout = isCheckout;
          } else {
            this.errorMessage = error;
          }
        }
      );
  }

  /**
   * method to check if checkout is selected or just accessories are added to cart
   * @param isCheckout boolean
   * @returns void
   */
  private navigateCheckout(isCheckout: boolean): void {
    const shipTo = this.objectService.get(
      this.stateUtilService.getShipFulfillment(),
      'shipTo',
      !this.isEpin
    );
    if (isCheckout) {
      if (!this.cartSupportService.shouldNavigateToPlanServices(shipTo)) {
        this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.CART]);
      }
    } else {
      this.stateUtilService.setShipFulfillment({ shipTo: shipTo });
      this.stateUtilService.setInstoreFlow(!shipTo);
      this.popItemsAddedPopUp();
    }
  }

  /**
   * popup management function
   * @returns void
   */
  private popItemsAddedPopUp(): void {
    const popupDuration = 3000;
    this.showItemAddedPopUp = true;
    setTimeout(
      () => {
        this.showItemAddedPopUp = false;
      },
      this.objectService.get(this.pdpStaticContent, 'popUpTimeOut') || popupDuration
    );
  }

  /**
   * Function to populate the button data for add to cart and checkout
   */
  private populateButtonData(): void {
    this.addToCart = {
      name: '',
      id: BUTTON_CONSTANTS.BACK_ID,
      class: BUTTON_CONSTANTS.PRIMARY_BUTTON,
      type: 'primary',
    };

    this.checkOut = {
      name: '',
      id: BUTTON_CONSTANTS.BACK_ID,
      class: BUTTON_CONSTANTS.SECONDARY_BUTTON,
      type: 'secondary',
    };
  }

  /**
   * Function used to get e-pin details specific to amount
   * @param {number} amount
   */
  public epinDetailsForSelectedAmount(amount: number): void {
    this.selectedEpinAmount = amount;
    this.selectedAmount = amount;
    const selectedEpinSkuNumber = this.pricingOptions.find((value: PricingOptions) => {
      return value.skuPrice === amount;
    }).skuId;
    this.selectedItem = this.product.getItemDetailsByDeviceSku(
      selectedEpinSkuNumber,
      this.objectService
    );
    // if (this.isWebChannel) {
    this.pdpDataHandlerService.updateEpinInfoOnPriceChange(this.selectedItem);
    // }
  }

  /**
   * creates a form for quantity dropdown and assigns a default selected quantity
   * @returns void
   */
  private createEpinPricingDropdown(): void {
    this.pricingListDropdown = [];
    const pricingOptionlength = this.pricingOptions.length;
    for (let key = 0; key < pricingOptionlength; key++) {
      const dropdownData = {
        label: this.currencyPipe.transform(
          this.pricingOptions[key].skuPrice.toString(),
          'USD',
          'symbol-narrow',
          '1.2-2'
        ),
        value: this.pricingOptions[key].skuPrice,
      };
      this.pricingListDropdown.push(dropdownData);
    }
  }
  /**
   * creates a response for epin details
   * @returns void
   */
  private createEpinDetails(): void {
    const selectedItem = this.selectedItem.sku || this.selectedItem.id;
    this.epinCount.selectedItem.sku = selectedItem;
    this.epinCount.deviceDetails.offerId = this.deviceDetails.offerId;
    this.epinCount.selectedQuantity = this.selectedQuantity;
    const tempEpin = this.selectedEpinAmount ? this.selectedEpinAmount.toString() : undefined;
    this.epinCount.selectedEpinAmount = tempEpin;
  }
  /**
   * to set the details of tile in focus on the basis of existing line LoS ID
   */
  private setExisitingTileInFocus(): void {
    const losId = this.stateUtilService.getSelectedUpgradeLineId();
    const tileId = this.tileManagementService.getExisitingLineTileId(losId);
    this.stateUtilService.setFocusedTileId(tileId);
    this.focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
  }
  /**
   * function to display multiline upgrade modal
   * @returns void
   */
  private showMultilineUpgradeModal(
    productDetails: ProductDetails,
    isDeviceUpgrade?: boolean
  ): void {
    this.userService
      .getCustomerAccountBillingSummary()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: CustomerAccountBillingDetails) => {
        this.cartExistingLines = this.objectService.get(response, `lines`);
        if (this.objectService.get(this.cartExistingLines, `length`) > 1) {
          this.displayUpgradeMultilineModal = true;
        } else if (this.objectService.get(this.cartExistingLines, `length`) === 1) {
          const lineId = this.objectService.get(this.cartExistingLines, `0.lineOfServiceId`);
          this.stateUtilService.setSelectedUpgradeLineId(lineId);
          this.setSelectedItemInPDP(productDetails, isDeviceUpgrade);
        }
      });
  }

  /**
   * Function to update line selected on multiline model
   * @param productDetails ProductDetails
   * @param isDeviceUpgrade boolean
   * @returns void
   */
  public updateUpgradeLineSelected(
    productDetails: ProductDetails,
    isDeviceUpgrade?: boolean
  ): void {
    this.displayUpgradeMultilineModal = false;
    this.setSelectedItemInPDP(productDetails, isDeviceUpgrade);
  }

  private converNonLineItemToLineItem(overrideIndicator?: string): void {
    const cartItemId = this.objectService.get(
      this.focusedTileIdDetails,
      'selectedDevice.cartItemId'
    );
    const lineId = this.stateUtilService.getSelectedUpgradeLineId();
    const newSku = this.objectService.get(this.selectedItem, 'id');
    const newOfferId = this.stateUtilService.getSelectedOfferId();
    const cart = this.stateUtilService.getCart();
    const transactionType = this.objectService.get(cart, 'orderType');
    this.cartSupportService
      .updateCartLevelDeviceToLineLevel(
        cartItemId,
        null,
        lineId,
        newSku,
        newOfferId,
        transactionType,
        PRODUCT_OFFERINGS_CONSTANTS.UPGRADE_DEVICE,
        overrideIndicator
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data: Cart) => {
          this.showSpinner = false;
          this.stateUtilService.setUpgradeFlow(true);
          this.callTriggerCartEvent();
          this.tileManagementService.mapToCartResponse(data);
          this.tileManagementService.deleteLine();
          this.setExisitingTileInFocus();
          this.navigateToProductSelector(true);
        },
        (error: APIError[]) => {
          this.errorMessage = error;
          this.upgradeErrorMessage = error;
          this.showSpinner = false;
        }
      );
  }

  /**
   * Close upgrade modal on click of cross button
   * @return void
   */
  public closeUpgradeModal(): void {
    this.displayUpgradeMultilineModal = false;
  }

  /**
   * Function to process the cart responce once item is added as non line
   * @param cartData Cart
   */
  private processNonLineItemCartResponse(cartData: Cart): void {
    this.stateUtilService.setSelectedItemInPDP(this.selectedItem);
    this.tileManagementService.setNonLineItemDetails(cartData);
    this.errorMessage = null;
    this.showSpinner = false;
    this.navigateToProductSelector();
  }

  private createModal(): void {
    this.mixedCartModal = {
      headerText: this.objectService.get(this.pdpStaticContent, 'clearYourCart'),
      bodyText: this.objectService.get(this.pdpStaticContent, 'clearOrViewCart'),
      disableDismiss: true,
      disableEsc: true,
      modalName: 'mixed-cart-modal',
      buttons: [
        {
          id: this.objectService.get(this.pdpStaticContent, 'continue'),
          type: 'submit',
          name: this.objectService.get(this.pdpStaticContent, 'continue'),
          class: 'btn btn-primary',
        },
        {
          id: 'ViewCart',
          type: 'cancel',
          name: this.objectService.get(this.pdpStaticContent, 'viewCart'),
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
  }

  /**
   * To check availability of device
   * @param isAvailable boolean to check availibility
   */
  public checkAvailability(isAvailable: boolean): void {
    this.isProductAvailable = isAvailable;
  }

  public handleClickEvent(event: Event): void {
    this.mixedCartModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    const key = 'id';
    const id = event[key];
    if (id === this.objectService.get(this.pdpStaticContent, 'continue')) {
      if (this.objectService.get(this.mixedCartObject, 'isAccessory')) {
        this.addAccessoriesToCart(
          this.objectService.get(this.mixedCartObject, 'isAccessoryCheckout'),
          true
        );
      } else {
        this.setSelectedItemInPDP(
          this.selectedProductDetails,
          this.selectedProductDeviceUpgrade,
          'true'
        );
      }
    } else if (id === this.objectService.get(this.pdpStaticContent, 'viewCart')) {
      this.mixedCart = false;
      this.router.navigate([HARDGOODS_CONSTANTS.CART_URL]);
    }
  }
  /**
   * Function to delete non line tile if it does not have a device associated with it
   */
  private deleteNonLineTileWithoutDevice(): void {
    const lineDetails = this.tileManagementService.getFocusedTileIdDetails();
    const lineTileType = this.objectService.get(lineDetails, 'lineTileType');
    const cartItemId = this.objectService.get(lineDetails, 'selectedDevice.cartItemId');
    if (lineTileType === lineTypeEnum.NONLINE && !cartItemId) {
      this.tileManagementService.deleteLine();
    }
  }

  private triggerCartAddEvent(modal: boolean, ctaName: string): void {
    // if (this.isWebChannel) {
    if (modal) {
      this.modal = {
        modalName: this.objectService.get(this.pdpStaticContent, 'areYouAnExistingCustomer', ''),
      };
    }
    const productInfo = this.pdpDataHandlerService.fetchProductInfo(
      this.stateUtilService.getCart(),
      this.product.skuId
    );
    this.pdlData = {
      ctaName: ctaName,
      product: productInfo,
    };
    const cart = this.stateUtilService.getCart();
    const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.pdpDataHandlerService.triggerCartEvent(
      cart,
      this.pdlPageComponentId,
      isCartEmpty,
      CART_EVENT_NAME.CARTADD
    );
    // }
  }

  private callTriggerCartEvent(): void {
    // if (this.isWebChannel) {
    if (this.isNewCustomer) {
      this.triggerCartAddEvent(
        true,
        this.objectService.get(this.pdpStaticContent, 'newCustomer', '')
      );
      this.isNewCustomer = false;
    } else if (this.isAuthFlow) {
      this.callCartEventForAuthFLow();
    } else if (this.isAccessory || this.isEpin) {
      this.checkoutAccessory
        ? this.triggerCartAddEvent(
            false,
            this.objectService.get(this.pdpStaticContent, 'checkout', '')
          )
        : this.triggerCartAddEvent(
            false,
            this.objectService.get(this.pdpStaticContent, 'addToCart', '')
          );
    } else {
      this.triggerCartAddEvent(false, this.objectService.get(this.pdpStaticContent, 'select', ''));
    }
    // }
  }

  private callCartEventForAuthFLow(): void {
    if (this.isEditingNonLineDevice) {
      this.triggerCartAddEvent(
        false,
        this.objectService.get(this.pdpStaticContent, 'replaceDevice', '')
      );
    } else if (this.isUpgradeFlow) {
      this.triggerCartAddEvent(
        false,
        this.objectService.get(this.pdpStaticContent, 'purchaseDevice', '')
      );
    } else if (!this.isP10MCustomer) {
      if (this.isUpgradeCart) {
        if (!this.isEditingNonLineDevice) {
          this.triggerCartAddEvent(
            false,
            this.objectService.get(this.pdpStaticContent, 'replaceDevice', '')
          );
        }
      } else if (this.isCartEmpty || this.isPurchaseEquipment) {
        this.triggerCartAddEvent(
          false,
          this.objectService.get(this.pdpStaticContent, 'upgradeCurrentDevice', '')
        );
      }
    }
  }
}
